/* eslint-disable no-nested-ternary, camelcase */
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query/react";

// Components
import {
  SectionCard,
  FieldText,
  FieldSelect,
  FieldToggle,
  DateRangePopover,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectTimeSelector,
  updateTimeSelector,
  openNotificationPopup,
} from "state/application/applicationSlice";
import {
  useGetGeneralParametersByCommunityIdQuery,
  useAddNewGeneralParameterMutation,
  useUpdateGeneralParameterByIdMutation,
} from "state/generalParameters/generalParametersApiSlice";
import { useGetEnergyTariffValueQuery } from "state/erse/tariffsApiSlice";
import {
  sizingOptimizationApiSlice,
  useBuildAndSimulatePayloadMutation,
} from "state/sizingOptimization/sizingOptimizationApiSlice";
import { useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";
import { contractsApiSlice } from "state/contracts/contractsApiSlice";
import {
  useAddMeterStaticParameterMutation,
  useUpdateMeterStaticParameterMutation,
  meterStaticParametersApi,
} from "state/meterParameters/meterStaticParametersApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType, addTask } from "state/tasks/tasksSlice";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

const SIMULATION_TASK_TYPE = "SIZING_SIMULATION";

const DEFAULT_STANDARD_COSTS = {
  l_gic: 0.009343915,
  l_bic: 0.000547945,
  l_cont: 0.042753623,
};

function GeneralParameters({ communityId, data, isLoading }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["sizing"]);
  const { startDate, endDate } = useSelector(selectTimeSelector);

  // ----- Run simulation (task polling) -----
  const [buildAndSimulate] = useBuildAndSimulatePayloadMutation();
  const activeTasks = useSelector(selectActiveTasksByType(SIMULATION_TASK_TYPE));

  const taskArgs =
    activeTasks.length > 0
      ? {
          taskId: activeTasks[0].id,
          communityId,
          groupTask: activeTasks[0].groupTask ? "true" : "false",
        }
      : skipToken;

  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(taskArgs, {
    pollingInterval: 3000,
    refetchOnFocus: true,
  });

  useTaskStatusEffect(
    {
      isSuccess,
      isFetching,
      taskStatus,
      activeLoadsTasks: activeTasks,
      dispatch,
      TASK_TYPE: SIMULATION_TASK_TYPE,
    },
    () => {
      dispatch(
        sizingOptimizationApiSlice.util.invalidateTags([
          { type: "SizingSimulation", id: communityId },
        ])
      );
    }
  );

  // ----- General parameters fetch + form state -----
  const {
    data: generalParametersData,
    isLoading: isLoadingGeneralParameters,
    refetch: refetchGeneralParameters,
  } = useGetGeneralParametersByCommunityIdQuery({ communityId }, { skip: !communityId });

  const calculatedNrDays =
    startDate && endDate
      ? Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
      : "";

  const [original, setOriginal] = useState(data || {});
  const [nrClusters, setNrClusters] = useState(data?.nr_clusters ?? "");
  const [lGrid, setLGrid] = useState(data?.l_grid ?? 0.1);
  const [deltaT, setDeltaT] = useState(data?.delta_t ?? 0.25);
  const [storageRatio, setStorageRatio] = useState(data?.storage_ratio ?? "");
  const [strictPosCoeffs, setStrictPosCoeffs] = useState(data?.strict_pos_coeffs ?? true);
  const [totalShareCoeffs, setTotalShareCoeffs] = useState(data?.total_share_coeffs ?? true);

  const [originalDefaults, setOriginalDefaults] = useState(DEFAULT_STANDARD_COSTS);
  const [lGicDefault, setLGicDefault] = useState(DEFAULT_STANDARD_COSTS.l_gic);
  const [lBicDefault, setLBicDefault] = useState(DEFAULT_STANDARD_COSTS.l_bic);
  const [lContDefault, setLContDefault] = useState(DEFAULT_STANDARD_COSTS.l_cont);

  const [isSubmittingSimulation, setIsSubmittingSimulation] = useState(false);

  // Tariff
  const { data: energyTariffValue } = useGetEnergyTariffValueQuery({
    validity: startDate,
    icVoltageLevel: "AT",
    iprVoltageLevel: "MAT",
    period: "PONTA",
    ciegDiscount: 0,
  });

  useEffect(() => {
    if (energyTariffValue) {
      setLGrid(parseFloat(energyTariffValue));
    }
  }, [energyTariffValue]);

  useEffect(() => {
    if (generalParametersData) {
      setNrClusters(generalParametersData.nr_clusters ?? "");
      setDeltaT(generalParametersData.delta_t ?? 0.25);
      setStorageRatio(generalParametersData.storage_ratio ?? "");
      setStrictPosCoeffs(generalParametersData.strict_pos_coeffs ?? true);
      setTotalShareCoeffs(generalParametersData.total_share_coeffs ?? true);
      if (!energyTariffValue) {
        setLGrid(generalParametersData.l_grid ?? 0.1);
      }
      setOriginal(generalParametersData);
    }
  }, [generalParametersData, energyTariffValue]);

  // ----- Save mutations -----
  const [addNewGeneralParameter, { isLoading: isAdding }] = useAddNewGeneralParameterMutation();
  const [updateGeneralParameter, { isLoading: isUpdating }] =
    useUpdateGeneralParameterByIdMutation();

  const { data: meterData } = useGetMetersByCommunityIdQuery({ communityId });
  const meters = meterData?.ids?.map((id) => meterData.entities[id]) ?? [];

  const [addMeterStaticParameter] = useAddMeterStaticParameterMutation();
  const [updateMeterStaticParameter] = useUpdateMeterStaticParameterMutation();

  const saving = isAdding || isUpdating;

  const defaultsChanged =
    parseFloat(lGicDefault) !== originalDefaults.l_gic ||
    parseFloat(lBicDefault) !== originalDefaults.l_bic ||
    parseFloat(lContDefault) !== originalDefaults.l_cont;

  const canSave =
    [lGrid, deltaT, storageRatio].every((val) => val !== "" && val !== null) &&
    !saving &&
    (original.nr_clusters !== nrClusters ||
      original.l_grid !== lGrid ||
      original.delta_t !== deltaT ||
      original.storage_ratio !== storageRatio ||
      original.strict_pos_coeffs !== strictPosCoeffs ||
      original.total_share_coeffs !== totalShareCoeffs ||
      defaultsChanged);

  const syncDefaultsToAllMeters = async () => {
    if (!Array.isArray(meters)) return;

    const updateTasks = meters.map(async (meter) => {
      try {
        if (meter.type !== "CPE") return null;

        const contractsResult = await dispatch(
          contractsApiSlice.endpoints.getContractsByMeterId.initiate({
            communityId,
            meterId: meter.id,
          })
        ).unwrap();
        const contractedPower = contractsResult?.[0]?.contracted_power ?? null;

        const defaults = {
          l_gic: parseFloat(lGicDefault),
          l_bic: parseFloat(lBicDefault),
          l_cont: parseFloat(lContDefault),
          ...(typeof contractedPower === "number" && contractedPower > 0
            ? { p_meter_max: parseFloat(contractedPower) }
            : {}),
        };

        const result = await dispatch(
          meterStaticParametersApi.endpoints.getMeterStaticParametersByMeterId.initiate({
            communityId,
            meterId: meter.id,
          })
        ).unwrap();

        const firstParamId = result?.ids?.[0];
        const staticData = result?.entities?.[firstParamId];

        if (firstParamId && staticData) {
          return await updateMeterStaticParameter({
            communityId,
            parameterId: firstParamId,
            updatedData: { ...staticData, ...defaults },
          }).unwrap();
        }
        return await addMeterStaticParameter({
          communityId,
          parameterData: {
            ...defaults,
            meter_id: meter.id,
            community_id: communityId,
          },
        }).unwrap();
      } catch {
        return null;
      }
    });

    await Promise.all(updateTasks);
  };

  const handleSave = async () => {
    if (!canSave) return;

    const nrClustersValue = nrClusters === "" ? 0 : parseInt(nrClusters, 10);
    const newData = {
      nr_days: parseInt(calculatedNrDays, 10),
      nr_clusters: parseInt(nrClustersValue, 10),
      l_grid: parseFloat(lGrid),
      delta_t: parseFloat(deltaT),
      storage_ratio: parseFloat(storageRatio),
      strict_pos_coeffs: strictPosCoeffs,
      total_share_coeffs: totalShareCoeffs,
    };

    try {
      if (!data || !data.id) {
        await addNewGeneralParameter({ community_id: communityId, ...newData }).unwrap();
      } else {
        await updateGeneralParameter({
          communityId,
          parameterId: data.id,
          updatedData: newData,
        }).unwrap();
      }
      setOriginal(newData);
      setOriginalDefaults({
        l_gic: parseFloat(lGicDefault),
        l_bic: parseFloat(lBicDefault),
        l_cont: parseFloat(lContDefault),
      });
      await syncDefaultsToAllMeters();
      refetchGeneralParameters();
      dispatch(
        openNotificationPopup({
          notification: t("generalParameters.form.buttons.save"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        openNotificationPopup({
          notification: err?.data?.detail || err?.message,
          type: "error",
        })
      );
    }
  };

  const handleRunSimulation = async () => {
    try {
      setIsSubmittingSimulation(true);
      const payload = { start_date: startDate, end_date: endDate };
      const result = await buildAndSimulate({ communityId, payload }).unwrap();

      if (result?.task_id) {
        dispatch(
          addTask({
            id: result.task_id,
            type: SIMULATION_TASK_TYPE,
            status: "PENDING",
          })
        );
      }
    } catch (err) {
      const errorMsg =
        err?.data?.detail ||
        t("generalParameters.errors.runSimulation", "Ocorreu um erro ao iniciar a simulação.");
      dispatch(openNotificationPopup({ notification: errorMsg, type: "error" }));
    } finally {
      setIsSubmittingSimulation(false);
    }
  };

  const isSimulationRunning = isSubmittingSimulation || activeTasks.length > 0 || isFetching;
  const isOverallLoading = isLoading || isLoadingGeneralParameters || isSubmittingSimulation;

  const loadDeltaTValue = () => {
    if (deltaT === 0.25) return "0.25";
    if (deltaT === 1) return "1";
    return "";
  };

  return (
    <SectionCard
      id="general-parameters"
      icon="tune"
      title={t("tool.tabs.generalParameters")}
      sub={t("tool.header.subtitle")}
    >
      {/* Optimization period */}
      <div className="mb-[18px] max-w-[340px]">
        <div className="text-[12.5px] font-semibold text-ink-2 mb-[6px]">
          {t("generalParameters.form.optimizationPeriod")}
        </div>
        <DateRangePopover
          value={{ start: startDate, end: endDate }}
          onChange={(v) => dispatch(updateTimeSelector({ startDate: v.start, endDate: v.end }))}
        />
      </div>

      {/* Community general parameters */}
      <div className="rec-subhead">{t("generalParameters.title")}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldText
          label={t("generalParameters.form.fields.nrClusters")}
          type="number"
          value={nrClusters}
          onChange={(v) => setNrClusters(v)}
        />
        <FieldText
          label={t("generalParameters.form.fields.lGrid")}
          type="number"
          suffix="€/kWh"
          value={lGrid}
          onChange={(v) => setLGrid(v)}
        />
        <FieldSelect
          label={t("generalParameters.form.fields.deltaT")}
          value={loadDeltaTValue()}
          onChange={(v) => setDeltaT(parseFloat(v))}
          options={[
            { value: "0.25", label: t("generalParameters.form.selects.mins15") },
            { value: "1", label: t("generalParameters.form.selects.hour1") },
          ]}
        />
        <FieldText
          label={t("generalParameters.form.fields.storageRatio")}
          type="number"
          hint={t("generalParameters.form.placeholders.storageRatio")}
          value={storageRatio}
          onChange={(v) => setStorageRatio(v)}
        />
      </div>

      <div className="rec-divider" />
      <div className="rec-subhead">{t("generalParameters.form.switches.title")}</div>
      <div className="flex flex-col gap-[10px]">
        <FieldToggle
          label={t("generalParameters.form.switches.strictPosCoeffs")}
          value={strictPosCoeffs}
          onChange={(v) => setStrictPosCoeffs(v)}
        />
        <FieldToggle
          label={t("generalParameters.form.switches.totalShareCoeffs")}
          value={totalShareCoeffs}
          onChange={(v) => setTotalShareCoeffs(v)}
        />
      </div>

      <div className="rec-divider" />
      <div className="rec-subhead">{t("generalParameters.form.standard_costs.title")}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldText
          label={t("generalParameters.form.standard_costs.lGicDefault")}
          type="number"
          hint={t("generalParameters.form.helperText.applyToAllMembers")}
          value={lGicDefault}
          onChange={(v) => setLGicDefault(v)}
        />
        <FieldText
          label={t("generalParameters.form.standard_costs.lBicDefault")}
          type="number"
          hint={t("generalParameters.form.helperText.applyToAllMembers")}
          value={lBicDefault}
          onChange={(v) => setLBicDefault(v)}
        />
        <FieldText
          label={t("generalParameters.form.standard_costs.lContDefault")}
          type="number"
          hint={t("generalParameters.form.helperText.applyToAllMembers")}
          value={lContDefault}
          onChange={(v) => setLContDefault(v)}
        />
      </div>

      <div className="rec-wiz-foot">
        <button
          type="button"
          className="rec-btn rec-btn-ghost"
          disabled={!canSave || isOverallLoading}
          onClick={handleSave}
        >
          <Icon name="save" />
          {saving
            ? t("generalParameters.form.buttons.saving")
            : t("generalParameters.form.buttons.save")}
        </button>
        <button
          type="button"
          className="rec-btn rec-btn-primary"
          disabled={isSimulationRunning || isOverallLoading}
          onClick={handleRunSimulation}
        >
          {isSimulationRunning ? (
            <>
              <span className="animate-spin inline-flex">
                <Icon name="progress_activity" />
              </span>
              {`${t("generalParameters.simulation.button.run")}...`}
            </>
          ) : (
            <>
              <Icon name="play_arrow" fill />
              {t("generalParameters.simulation.button.run")}
            </>
          )}
        </button>
      </div>
    </SectionCard>
  );
}

GeneralParameters.propTypes = {
  communityId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    id: PropTypes.string,
    nr_clusters: PropTypes.number,
    l_grid: PropTypes.number,
    delta_t: PropTypes.number,
    storage_ratio: PropTypes.number,
    strict_pos_coeffs: PropTypes.bool,
    total_share_coeffs: PropTypes.bool,
  }),
  isLoading: PropTypes.bool,
};

GeneralParameters.defaultProps = {
  data: {
    id: null,
    nr_clusters: 0,
    l_grid: 0.1,
    delta_t: 0.25,
    storage_ratio: 1,
    strict_pos_coeffs: true,
    total_share_coeffs: true,
  },
  isLoading: false,
};

export default GeneralParameters;
